import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi';
import { toast } from './toast';
import { cn } from './cn';

const VARIANT_STYLES = {
  success: {
    icon: FiCheckCircle,
    ring: 'ring-success-100',
    iconColor: 'text-success-600',
  },
  danger: {
    icon: FiAlertCircle,
    ring: 'ring-danger-100',
    iconColor: 'text-danger-600',
  },
  warning: {
    icon: FiAlertTriangle,
    ring: 'ring-warning-100',
    iconColor: 'text-warning-600',
  },
  info: {
    icon: FiInfo,
    ring: 'ring-info-100',
    iconColor: 'text-info-600',
  },
};

/**
 * ToastProvider — mounts once near the app root. Subscribes to the toast
 * emitter and renders a stacking list bottom-right.
 */
export function ToastProvider() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    return toast._subscribe((t) => {
      if (t.__dismiss) {
        setItems((prev) => prev.filter((x) => x.id !== t.id));
        return;
      }
      setItems((prev) => [...prev, t]);
      if (t.duration && t.duration > 0) {
        setTimeout(() => {
          setItems((prev) => prev.filter((x) => x.id !== t.id));
        }, t.duration);
      }
    });
  }, []);

  return createPortal(
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
    >
      <AnimatePresence initial={false}>
        {items.map((t) => {
          const v = VARIANT_STYLES[t.variant] || VARIANT_STYLES.info;
          const Icon = v.icon;
          return (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 24, transition: { duration: 0.15 } }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className={cn(
                'pointer-events-auto relative flex items-start gap-3',
                'rounded-lg bg-surface p-3 pr-8 shadow-e2 ring-1',
                v.ring,
              )}
            >
              <Icon size={18} className={cn('shrink-0 mt-0.5', v.iconColor)} aria-hidden />
              <div className="min-w-0 flex-1">
                {t.title && <p className="text-body-sm font-medium text-fg">{t.title}</p>}
                {t.description && (
                  <p className="mt-0.5 text-caption text-fg-muted">{t.description}</p>
                )}
                {t.action && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        t.action.onClick?.();
                        toast.dismiss(t.id);
                      }}
                      className="text-caption font-semibold text-brand-600 hover:underline"
                    >
                      {t.action.label}
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => toast.dismiss(t.id)}
                className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded text-fg-subtle hover:bg-ink-100 hover:text-fg"
              >
                <FiX size={14} />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>,
    document.body,
  );
}

export default ToastProvider;
